import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, ModelSetting, AvailableModel } from '../../services/admin';

// Provider labels for display
const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
};

// Setting keys for looking up translations
const SETTING_KEYS: Record<string, { titleKey: string; descKey: string }> = {
  deep_analysis_orchestrator: {
    titleKey: 'adminModels.orchestratorTitle',
    descKey: 'adminModels.orchestratorDesc',
  },
  judgment_analysis: {
    titleKey: 'adminModels.judgmentAnalysisTitle',
    descKey: 'adminModels.judgmentAnalysisDesc',
  },
  use_openrouter: {
    titleKey: 'adminModels.openRouterTitle',
    descKey: 'adminModels.openRouterDesc',
  },
};

export default function AdminModelsPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ModelSetting[]>([]);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [settingsRes, modelsRes] = await Promise.all([
        adminAPI.getModelSettings(),
        adminAPI.getAvailableModels(),
      ]);
      setSettings(settingsRes.settings);
      setAvailableModels(modelsRes.models);
    } catch (err) {
      setError(t('errors.loadSettings'));
      console.error('Failed to fetch model settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModelChange = async (settingKey: string, modelId: string) => {
    try {
      setIsSaving(settingKey);
      setError(null);
      await adminAPI.updateModelSetting(settingKey, { modelId });
      setSettings(prev =>
        prev.map(s =>
          s.settingKey === settingKey
            ? { ...s, settingValue: { ...s.settingValue, modelId } }
            : s
        )
      );
      showSuccess(t('adminModels.settingSaved'));
    } catch (err) {
      setError(t('errors.saveSetting'));
      console.error('Failed to update model setting:', err);
    } finally {
      setIsSaving(null);
    }
  };

  const handleToggleChange = async (settingKey: string, enabled: boolean) => {
    try {
      setIsSaving(settingKey);
      setError(null);
      await adminAPI.updateModelSetting(settingKey, { enabled });
      setSettings(prev =>
        prev.map(s =>
          s.settingKey === settingKey
            ? { ...s, settingValue: { ...s.settingValue, enabled } }
            : s
        )
      );
      showSuccess(enabled ? t('adminModels.openRouterEnabled') : t('adminModels.openRouterDisabled'));
    } catch (err) {
      setError(t('errors.saveSetting'));
      console.error('Failed to update toggle setting:', err);
    } finally {
      setIsSaving(null);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const getSettingValue = (key: string) => {
    const setting = settings.find(s => s.settingKey === key);
    return setting?.settingValue;
  };

  const getModelById = (id: string | undefined): AvailableModel | null => {
    if (!id) return null;
    return availableModels.find(m => m.id === id) || null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const openRouterValue = getSettingValue('use_openrouter');
  const isOpenRouterEnabled = openRouterValue?.enabled === true;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('adminModels.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {t('adminModels.subtitle')}
        </p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {/* Global Settings */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminModels.globalSettings')}</h2>

        {/* OpenRouter Toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
          <div className="flex-1">
            <h3 className="font-medium text-slate-900 dark:text-white">
              {t('adminModels.openRouterTitle')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('adminModels.openRouterDesc')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isSaving === 'use_openrouter' && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            )}
            <button
              onClick={() => handleToggleChange('use_openrouter', !isOpenRouterEnabled)}
              disabled={isSaving === 'use_openrouter'}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isOpenRouterEnabled
                  ? 'bg-blue-600'
                  : 'bg-slate-300 dark:bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isOpenRouterEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${
              isOpenRouterEnabled
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-slate-500 dark:text-slate-400'
            }`}>
              {isOpenRouterEnabled ? t('common.enabled') : t('common.disabled')}
            </span>
          </div>
        </div>

        {isOpenRouterEnabled && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Info:</strong> {t('adminModels.openRouterInfo')}
            </p>
          </div>
        )}
      </div>

      {/* Deep Analysis Models */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminModels.deepAnalysisModels')}</h2>

        <div className="space-y-6">
          {/* Orchestrator Model */}
          <ModelSelector
            settingKey="deep_analysis_orchestrator"
            title={t(SETTING_KEYS.deep_analysis_orchestrator.titleKey)}
            description={t(SETTING_KEYS.deep_analysis_orchestrator.descKey)}
            currentModelId={getSettingValue('deep_analysis_orchestrator')?.modelId}
            availableModels={availableModels}
            onModelChange={handleModelChange}
            isSaving={isSaving === 'deep_analysis_orchestrator'}
            getModelById={getModelById}
          />

          {/* Judgment Analysis Model */}
          <ModelSelector
            settingKey="judgment_analysis"
            title={t(SETTING_KEYS.judgment_analysis.titleKey)}
            description={t(SETTING_KEYS.judgment_analysis.descKey)}
            currentModelId={getSettingValue('judgment_analysis')?.modelId}
            availableModels={availableModels}
            onModelChange={handleModelChange}
            isSaving={isSaving === 'judgment_analysis'}
            getModelById={getModelById}
          />
        </div>
      </div>

      {/* Available Models Reference */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminModels.availableModels')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-2 text-sm font-medium text-slate-500 dark:text-slate-400">{t('adminModels.id')}</th>
                <th className="text-left py-2 text-sm font-medium text-slate-500 dark:text-slate-400">{t('adminModels.modelName')}</th>
                <th className="text-left py-2 text-sm font-medium text-slate-500 dark:text-slate-400">{t('adminModels.provider')}</th>
                <th className="text-left py-2 text-sm font-medium text-slate-500 dark:text-slate-400">{t('common.description')}</th>
              </tr>
            </thead>
            <tbody>
              {availableModels.map((model) => (
                <tr key={model.id} className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="py-3 text-slate-900 dark:text-white font-mono text-sm">
                    {model.id}
                  </td>
                  <td className="py-3 text-slate-900 dark:text-white">
                    {model.displayName}
                  </td>
                  <td className="py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      model.provider === 'openai'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                    }`}>
                      {PROVIDER_LABELS[model.provider]}
                    </span>
                  </td>
                  <td className="py-3 text-slate-600 dark:text-slate-400 text-sm">
                    {model.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Model Selector Component
interface ModelSelectorProps {
  settingKey: string;
  title: string;
  description: string;
  currentModelId: string | undefined;
  availableModels: AvailableModel[];
  onModelChange: (key: string, modelId: string) => void;
  isSaving: boolean;
  getModelById: (id: string | undefined) => AvailableModel | null;
}

function ModelSelector({
  settingKey,
  title,
  description,
  currentModelId,
  availableModels,
  onModelChange,
  isSaving,
  getModelById,
}: ModelSelectorProps) {
  const { t } = useTranslation();
  const currentModel = getModelById(currentModelId);

  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="font-medium text-slate-900 dark:text-white">{title}</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{description}</p>
          {currentModel && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
              {t('adminModels.currently')} <span className="font-medium">{currentModel.displayName}</span> ({PROVIDER_LABELS[currentModel.provider]})
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isSaving && (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
          )}
          <select
            value={currentModelId || ''}
            onChange={(e) => onModelChange(settingKey, e.target.value)}
            disabled={isSaving}
            className="block w-48 px-3 py-2 bg-white dark:bg-slate-600 border border-slate-300 dark:border-slate-500 rounded-lg text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
          >
            {availableModels.map((model) => (
              <option key={model.id} value={model.id}>
                {model.displayName} ({PROVIDER_LABELS[model.provider]})
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
